import React, { useMemo, useState, useCallback, useRef } from 'react'
import './order-dialog.scss'
import { BaseDialog } from '@/components/dialog/base-dialog'
import { layerManage, showLayer } from '@/utils/layer'
import { Row, Column } from '@/components/layout/layout'
import useBemClass from '@/utils/hooks/useBEMClass'
import classnames from 'classnames'
import { useRequest } from 'ahooks'
import { IMallOrder } from '../type'
import { IPagination } from '@/model/types/common'
import { $post } from '@/api'
import { errorCallBack, get } from '@/utils'
import { mallOrderTypeTag } from '../order-list/order-table/order-table'
import { WithScrollLoad } from '@/utils/utils-widget'
import { Table } from 'antd'
import Button from '@/components/button/button'
import MallOrderDetail from '../order-list/order-detail/order-detail'
import Iconfont from '@/components/iconfont/iconfont'
import { ColumnsType } from 'antd/lib/table'
import $msg from '@/utils/message'
import PopoverConfirm from '@/components/popover-confirm/popover-confirm'
import Wave from 'antd/es/_util/wave'
import mallOrderStatus from './order-status-action'
import { mallOrderFilterStore } from '../order-filter/filter-store'
import PopverConfirm2 from '@/components/popover-confirm/popver-confirm-2'
import MallOrderFilter from '../order-filter/order-filter'
import { observer } from 'mobx-react'
import newUserImg from '@/assets/images/new-user.svg'
import { expressCompanyList } from '../order-deilver/deliver-company/deliver-company-info'
import { mallOrderStore } from '../store'

interface MallOrderDialogProps {}

const isRefundedOrder = (order: IMallOrder.OnlineOrder) => [2, 4].includes(order.order_info.refund_status)

const MallOrderDialog: React.FC<MallOrderDialogProps> = () => {
    const cls = useBemClass('mall-order-dialog')
    const [currentStatus, setCurrentStatus] = useState<number>(-1)
    const [selectKeys, setSelectKeys] = useState<string[]>([])
    const [allOrderNos, setAllOrderNos] = useState<string[]>([])
    const [visible, setVisible] = useState(false)
    const isChangedTab = useRef<boolean>(false)
    const hidden = () => {
        setVisible(false)
    }

    const orderCountReq = useRequest<{
        accepted_count: number
        apply_refund_count: number
        finished_count: number
        handling_count: number
        supplement_count: number
        team_purchase_user_count: number
    }>(
        () =>
            $post({
                url: '/mall/getonlineorderscount',
            }),
        {
            onSuccess: data => {
                if (!isChangedTab.current) {
                    if (data.apply_refund_count > 0) {
                        setCurrentStatus(4)
                    } else {
                        setCurrentStatus(1)
                    }
                    isChangedTab.current = true
                }
            },
            refreshDeps: [mallOrderStore.orderListRefresh],
            onError: errorCallBack,
        },
    )

    const statusMap = useMemo(
        () => [
            {
                id: 4,
                name: '退款申请',
                count: orderCountReq.data?.apply_refund_count,
            },
            {
                id: 10,
                name: '待补款',
                count: orderCountReq.data?.supplement_count,
            },
            {
                id: 1,
                name: '待处理',
                count: orderCountReq.data?.accepted_count,
            },
            {
                id: 2,
                name: '处理中',
                count: orderCountReq.data?.handling_count,
            },
        ],
        [orderCountReq.data],
    )

    const requestHandle = useRequest<
        {
            list: IMallOrder.OnlineOrder[]
            can_bulk_order_nos: string[]
            pagination: IPagination
        },
        {
            orders: IMallOrder.OnlineOrder[]
            can_bulk_order_nos: string[]
            pagination: IPagination
        }
    >(
        val => {
            return currentStatus !== -1
                ? $post({
                      url: '/mall/getshoponlineorders',
                      params: {
                          limit: 20,
                          page: val?.pagination?.page ? val.pagination.page + 1 : 1,
                          from_date: mallOrderFilterStore.date.length ? mallOrderFilterStore.date[0] : void 0,
                          to_date: mallOrderFilterStore.date.length ? mallOrderFilterStore.date[1] : void 0,
                          keyword: mallOrderFilterStore.keyWord || void 0,
                          ...mallOrderFilterStore.filterQuery,
                          status_list: mallOrderFilterStore.filterTxt
                              ? mallOrderFilterStore.filterQuery.status_list
                              : [currentStatus],
                      },
                  })
                : new Promise(() => {})
        },
        {
            refreshDeps: [
                currentStatus,
                mallOrderFilterStore.filterMap,
                mallOrderFilterStore.filterQuery,
                mallOrderFilterStore.date,
                mallOrderFilterStore.keyWord,
                mallOrderStore.orderListRefresh,
            ],
            isNoMore: data => {
                if (data?.pagination) {
                    return data?.pagination.page === data?.pagination.page_count
                } else {
                    return false
                }
            },
            loadMore: true,
            onSuccess: data => {
                setAllOrderNos(data.can_bulk_order_nos)
            },
            formatResult: data => {
                const visibleOrders = data.orders.filter(order => !isRefundedOrder(order))
                const visibleOrderNos = new Set(visibleOrders.map(order => order.order_info.order_no))

                return {
                    list: visibleOrders,
                    can_bulk_order_nos: data.can_bulk_order_nos.filter(orderNo => visibleOrderNos.has(orderNo)),
                    pagination: data.pagination,
                }
            },
            onError: errorCallBack,
        },
    )

    const userInfo = useCallback(
        (user: IMallOrder.Customer, showTag: boolean) => (
            <Row data-align-items-center>
                <div data-position-relative>
                    <img
                        style={{
                            border: `1px solid ${user.gender === 2 ? '#ee5b5b' : '#3c90ff'}`,
                        }}
                        className={cls('avatar')}
                        src={user.avatar}
                        alt=""
                    />

                    {showTag && <img className={cls('avatar', 'tag')} src={newUserImg} alt="" />}
                </div>
                <div className={cls('nickname')}>{user.nickname}</div>
            </Row>
        ),
        [cls],
    )

    const getGoodName = useCallback((name: string[]) => {
        if (name.length > 3) {
            return `${name.slice(0, 3).join('、')}...等${name.length}种`
        } else {
            return name.join('、')
        }
    }, [])

    const statusRefresh = useCallback(() => {
        requestHandle.refresh()
        orderCountReq.run()
    }, [orderCountReq, requestHandle])

    const statusAction = useCallback(
        (order: IMallOrder.OrderInfo) => {
            const status = (type?: 'accpet' | 'refuse') => mallOrderStatus(order, statusRefresh, type)

            if (order.status === 2 && order.refund_status === 0) {
                if (order.delivery_type !== 0) {
                    return (
                        <div
                            className={cls('bule-btn')}
                            onClick={() => {
                                // @ts-ignore
                                status()?.action && status().action()
                            }}
                        >
                            发货
                        </div>
                    )
                } else {
                    return (
                        <div
                            className={cls('green-btn')}
                            onClick={() => {
                                // @ts-ignore
                                status()?.action && status().action()
                            }}
                        >
                            通知提货
                        </div>
                    )
                }
            }
            if (order.status === 3 && order.refund_status === 0) {
                if (order.delivery_type === 1) {
                    return <div className={cls('bule-btn')}>确认送达</div>
                } else if (order.delivery_type === 2) {
                    const hasExpress = order.express_info instanceof Array

                    return hasExpress ? (
                        <Row data-aling-items-center>
                            {order.express_info.map(express => (
                                <Column key={express.express_company} data-margin-right-8>
                                    <img
                                        width="15"
                                        height="15"
                                        src={expressCompanyList.find(e => e.id === express.express_company)?.img}
                                        alt=""
                                    />
                                    <div data-color-999 data-margin-top-4 data-font-szie-14>
                                        商品已送出
                                    </div>
                                </Column>
                            ))}
                        </Row>
                    ) : (
                        <div data-color-999 data-margin-top-4 data-font-szie-14>
                            商品已送出
                        </div>
                    )
                } else {
                    return (
                        <div
                            className={cls('green-btn')}
                            onClick={() => {
                                // @ts-ignore
                                status()?.action && status().action()
                            }}
                        >
                            提货完成
                        </div>
                    )
                }
            }
            if (order.refund_status === 1) {
                return (
                    <>
                        <PopverConfirm2
                            trigger="click"
                            types="error"
                            placement="topRight"
                            onConfirm={() => {
                                // @ts-ignore
                                status('accpet')?.action && status('accpet').action()
                            }}
                            content={
                                <div>
                                    <div data-color-000 data-font-size-14>
                                        确定退款吗？
                                    </div>
                                    <div data-color-ff4343 data-font-size-14>
                                        ￥{order.actual_payment} 将以原支付方式退回
                                    </div>
                                </div>
                            }
                        >
                            <div className={cls('refund-accpet')}>退款</div>
                        </PopverConfirm2>
                        <PopverConfirm2
                            trigger="click"
                            types="error"
                            placement="topRight"
                            onConfirm={() => {
                                // @ts-ignore
                                status('refuse')?.action && status('refuse').action()
                            }}
                            content={
                                <div>
                                    <div data-color-000 data-font-size-14>
                                        确定
                                        <span data-color-ff4343 data-font-weight-600>
                                            拒绝退款
                                        </span>
                                        吗？
                                    </div>
                                </div>
                            }
                        >
                            <div className={cls('refund-refuse')}>拒绝</div>
                        </PopverConfirm2>
                    </>
                )
            }

            return <div />
        },
        [cls, statusRefresh],
    )

    const columns: ColumnsType<IMallOrder.OnlineOrder> = useMemo(
        () => [
            {
                title: '#',
                key: 'index',
                align: 'center',
                width: 40,
                render: (value, record, index) => index + 1,
            },
            {
                title: '下单时间',
                width: 110,
                render: (v, r) => r.order_info.create_time,
            },
            {
                title: '顾客昵称',
                width: 130,
                render: (v, r) => userInfo(r.order_info.customer, r.order_info.first_order),
            },
            {
                title: '订单类型',
                width: 80,
                render: (v, r) => {
                    const src = get(mallOrderTypeTag, r.has_seckill_goods ? '5' : `${r.order_info.order_type}`, '')

                    return <Row data-align-items-center>{src ? <img src={src} alt="" /> : '-'}</Row>
                },
            },
            {
                title: '配送类型',
                render: (v, r) => {
                    return r.order_info.delivery_type === 0 ? (
                        <Row data-align-items-center>
                            <Iconfont name="icon13ziti" size={20} />
                            <span data-color-000 data-font-weight-600 data-margin-left-4>
                                自提
                            </span>
                        </Row>
                    ) : (
                        <Column data-padding-v-8>
                            <Row data-align-items-center>
                                <Iconfont name="icon13peisong" size={20} />
                                <span data-color-000 data-font-weight-600 data-margin-left-4>
                                    配送
                                </span>
                            </Row>
                            <span className={cls('address')}>{r.order_info.address}</span>
                        </Column>
                    )
                },
            },
            {
                title: '商品信息',
                width: 260,
                render: (v, r) => getGoodName(r.order_goods_names),
            },
            {
                title: '总计',
                dataIndex: 'subtotal',
                width: 100,
                align: 'right',
                render: (v, r) => `¥${+r.order_info.subtotal}`,
            },
            {
                title: '操作',
                key: 'is_draft',
                align: 'center',
                render: (val, record) => (
                    <Row data-width-100p data-justify-content-center>
                        <Row
                            style={{
                                minWidth: '120px',
                            }}
                            data-justify-content-space-between
                            data-align-items-center
                        >
                            <Wave>{statusAction(record.order_info)}</Wave>
                            <div
                                className="action"
                                onClick={() => {
                                    showLayer(
                                        <MallOrderDetail
                                            orderId={record.order_info.order_id}
                                            orderType={record.order_info.order_type}
                                        />,
                                    )
                                }}
                            >
                                详情
                            </div>
                        </Row>
                    </Row>
                ),
            },
        ],
        [cls, getGoodName, statusAction, userInfo],
    )

    const batchOrderReq = useRequest(
        () =>
            $post({
                url: '/mall/orderbulkdeliver',
                params: {
                    order_nos: selectKeys,
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '处理成功')
                setSelectKeys([])
                statusRefresh()
                mallOrderStore.setOrderListRefresh()
            },
            onError: errorCallBack,
        },
    )

    const showRowSelection = useMemo(() => {
        return (
            [1, 2].includes(currentStatus) ||
            !mallOrderFilterStore.filterQuery.status_list ||
            mallOrderFilterStore.filterQuery.status_list.includes(2) ||
            mallOrderFilterStore.filterQuery.status_list.includes(1)
        )
    }, [currentStatus])

    return (
        <BaseDialog isShow width={1260} title="未完成订单" onClose={() => layerManage.pop()}>
            <Row data-align-items-center data-justify-content-space-between>
                {!mallOrderFilterStore.filterTxt && (
                    <Row data-align-items-center className={cls('tab-container')} data-margin-left-16>
                        {statusMap.map(s => (
                            <div
                                className={classnames(cls('tab'), {
                                    [cls('tab-active')]: s.id === currentStatus,
                                })}
                                key={s.id}
                                onClick={() => {
                                    setCurrentStatus(s.id)
                                }}
                            >
                                {s.name}
                                {!!s.count && <div className={cls('tab-badge')}>{s.count}</div>}
                            </div>
                        ))}
                    </Row>
                )}
                <div
                    className={cls('filter')}
                    data-cursor-pointer
                    onClick={() => {
                        showLayer(<MallOrderFilter />)
                    }}
                    style={{
                        color: !mallOrderFilterStore.filterTxt ? '#333' : '#14cc9b',
                    }}
                >
                    <i className="iconfont icon6shaixuan-20" data-font-size-20 />
                    <span data-margin-left-8>筛选</span>
                </div>
            </Row>
            {(mallOrderFilterStore.filterTxt || mallOrderFilterStore.keyWord) && (
                <Row className={cls('searchBox')}>
                    <Row>
                        {mallOrderFilterStore.keyWord && (
                            <div>
                                搜索：
                                <span data-font-weight-600 className="them-color" data-padding-h-8>
                                    {mallOrderFilterStore.keyWord}
                                </span>
                            </div>
                        )}
                        {mallOrderFilterStore.filterTxt && (
                            <div>
                                筛选：
                                <span data-font-weight-600 className="them-color" data-padding-h-8>
                                    {mallOrderFilterStore.filterTxt}
                                </span>
                            </div>
                        )}
                    </Row>
                    <div
                        className={cls('delete')}
                        onClick={() => {
                            mallOrderFilterStore.setkeyWord('')
                            mallOrderFilterStore.resetFilterMap()
                        }}
                    >
                        <i data-cursor-pointer className="iconfont icon6guanbi-20" />
                    </div>
                </Row>
            )}
            <div className={cls('table-container')} data-position-relative>
                <WithScrollLoad data-expaned data-overflow-hidden scrollloadhandle={requestHandle}>
                    <Table
                        rowKey={record => record.order_info.order_no}
                        loading={requestHandle.loading || requestHandle.loadingMore || void 0}
                        bordered
                        pagination={false}
                        columns={columns}
                        dataSource={requestHandle.data?.list}
                        scroll={{ x: 'max-content', y: requestHandle.data?.can_bulk_order_nos?.length ? 318 : 378 }}
                        rowSelection={
                            // 仅在处理中和待处理状态显示选择选项
                            showRowSelection
                                ? {
                                      type: 'checkbox',
                                      selectedRowKeys: selectKeys,
                                      onChange: selectedRowKeys => {
                                          setSelectKeys(selectedRowKeys as string[])
                                      },
                                      onSelectAll: selected => {
                                          setSelectKeys(selected ? allOrderNos || [] : [])
                                      },
                                      getCheckboxProps: record => ({
                                          // 禁止情况： 1. 未上架 2. 套餐商品
                                          disabled: !allOrderNos.includes(record.order_info.order_no),
                                      }),
                                  }
                                : void 0
                        }
                    />
                </WithScrollLoad>
                {!!allOrderNos.length && (
                    <div className={cls('bottom')}>
                        <div data-color-333 data-font-size-14 data-margin-right-40>
                            共{allOrderNos.length}个可处理订单，已选{' '}
                            <span className="them-color">
                                {selectKeys.length}/{allOrderNos.length}
                            </span>
                        </div>
                        <PopoverConfirm
                            trigger="click"
                            visible={visible}
                            types="error"
                            onVisibleChange={visible => {
                                setVisible(visible)
                            }}
                            onCancel={hidden}
                            onConfirm={() => {
                                hidden()
                                batchOrderReq.run()
                            }}
                            content={
                                <div>
                                    <div data-color-000 data-font-size-14>
                                        确认批量处理已选订单吗？
                                    </div>
                                    <div data-color-999 data-font-size-12>
                                        确认后，已选订单将处理至下一状态
                                    </div>
                                </div>
                            }
                        >
                            <Button types="default" loading={batchOrderReq.loading} disabled={!allOrderNos.length}>
                                批量处理至下一状态
                            </Button>
                        </PopoverConfirm>
                    </div>
                )}
            </div>
            <div data-color-999 data-font-size-14 data-margin-top-10 data-margin-bottom-16 data-margin-left-16>
                *选择任意 [待处理自提订单] 或 [处理中自提/配送订单]，可对订单进行批量操作。建议配合订单筛选一起使用
            </div>
        </BaseDialog>
    )
}

export default observer(MallOrderDialog)
